<template>
  <div class="pixso-page automotive-products">
    <main class="automotive-products__main">
      <section
        class="automotive-products__hero"
        aria-labelledby="automotive-products-title"
      >
        <div class="automotive-products__hero-copy">
          <h1 id="automotive-products-title" class="automotive-products__title">
            <span>{{ t('AutomotiveIndex.hero.line1') }}</span>
            <span>{{ t('AutomotiveIndex.hero.line2') }}</span>
          </h1>
          <p class="automotive-products__desc">
            {{ t('AutomotiveIndex.hero.description') }}
          </p>
        </div>
      </section>

      <section class="automotive-products__series pixso-stage" :aria-label="t('AutomotiveIndex.series.ariaLabel')">
        <div class="automotive-products__series-grid">
          <router-link
            v-for="item in seriesItems"
            :key="item.title"
            class="automotive-products__series-card"
            :to="item.to"
            :aria-label="t('AutomotiveIndex.series.moreAria', { title: item.title, subtitle: item.subtitle })"
          >
            <img class="automotive-products__series-card-image" :src="item.image" :alt="item.alt" />
            <span class="automotive-products__series-card-copy">
              <span class="automotive-products__series-card-title">{{ item.title }}</span>
              <span class="automotive-products__series-card-subtitle">{{ item.subtitle }}</span>
              <span class="automotive-products__series-card-more">{{ t('AutomotiveIndex.series.link') }} &gt;</span>
            </span>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import gnssSeriesImage from '@/assets/images/pixso-automotive-products/gnss-series-hero.jpg'
import imuSeriesImage from '@/assets/images/pixso-automotive-official/series-imu.jpg'
import insSeriesImage from '@/assets/images/pixso-automotive-official/series-ins.jpg'
import pboxSeriesImage from '@/assets/images/pixso-automotive-official/series-pbox.jpg'

const { t } = useI18n()

const seriesItems = computed(() => [
  {
    title: t('AutomotiveIndex.series.items.imu.title'),
    subtitle: t('AutomotiveIndex.series.items.imu.subtitle'),
    alt: t('AutomotiveIndex.series.items.imu.alt'),
    image: imuSeriesImage,
    to: '/product/automotive/imu'
  },
  {
    title: t('AutomotiveIndex.series.items.gnss.title'),
    subtitle: t('AutomotiveIndex.series.items.gnss.subtitle'),
    alt: t('AutomotiveIndex.series.items.gnss.alt'),
    image: gnssSeriesImage,
    to: '/product/automotive/gnss'
  },
  {
    title: t('AutomotiveIndex.series.items.pbox.title'),
    subtitle: t('AutomotiveIndex.series.items.pbox.subtitle'),
    alt: t('AutomotiveIndex.series.items.pbox.alt'),
    image: pboxSeriesImage,
    to: '/product/automotive/pbox'
  },
  {
    title: t('AutomotiveIndex.series.items.ins.title'),
    subtitle: t('AutomotiveIndex.series.items.ins.subtitle'),
    alt: t('AutomotiveIndex.series.items.ins.alt'),
    image: insSeriesImage,
    to: '/product/automotive/ins'
  }
])
</script>

<style scoped>
.automotive-products {
  --automotive-products-hero-overlay: url("@/assets/images/pixso-automotive-products/hero-overlay.png");
  --automotive-products-hero-copy-max: 1200px;
  --automotive-products-desc-max: 1036px;
  --automotive-products-card-ratio: 884 / 811;
  --automotive-products-card-row-gap: calc(var(--pixso-gap-lg) + var(--pixso-fs-16));
  --automotive-products-title-tablet-size: var(--pixso-fs-72);
  --automotive-products-title-tablet-line: var(--pixso-lh-72);
  --automotive-products-title-mobile-size: var(--pixso-fs-36);
  --automotive-products-title-mobile-line: var(--pixso-lh-36);
  --automotive-products-card-title-mobile-size: var(--pixso-fs-24);
  --automotive-products-card-title-mobile-line: var(--pixso-lh-24);
  --automotive-products-mobile-text-size: var(--pixso-fs-14);
  --automotive-products-mobile-text-line: var(--pixso-lh-14);
  --automotive-products-series-bottom-tablet: 220px;
  --automotive-products-series-bottom-mobile: 120px;
}

.automotive-products__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.automotive-products__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: var(--automotive-products-hero-overlay);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;
}

.automotive-products__hero-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--automotive-products-hero-copy-max));
  padding: 0 var(--pixso-fs-96);
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-md);
  text-align: center;
}

.automotive-products__title {
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  letter-spacing: 0;
}

.automotive-products__desc {
  width: min(100%, var(--automotive-products-desc-max));
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-20);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
  letter-spacing: 0;
}

.automotive-products__series {
  padding: var(--pixso-fs-96) var(--pixso-page-padding) var(--pixso-section-gap-xl);
  background: var(--pixso-color-bg);
}

.automotive-products__series-grid {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--automotive-products-card-row-gap) var(--pixso-gap-md);
}

.automotive-products__series-card {
  position: relative;
  aspect-ratio: var(--automotive-products-card-ratio);
  display: block;
  overflow: hidden;
  border-radius: var(--pixso-radius-lg);
  color: var(--pixso-color-text);
  text-decoration: none;
  background: var(--pixso-color-surface);
  cursor: pointer;
}

.automotive-products__series-card-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.automotive-products__series-card-image {
  object-fit: cover;
}

.automotive-products__series-card-copy {
  position: absolute;
  left: var(--pixso-gap-lg);
  right: var(--pixso-gap-lg);
  bottom: var(--pixso-gap-lg);
  display: flex;
  flex-direction: column;
  gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  align-items: center;
  text-align: center;
}

.automotive-products__series-card-title {
  font-size: var(--pixso-fs-36);
  font-weight: 700;
  line-height: var(--pixso-lh-36);
}

.automotive-products__series-card-subtitle {
  font-size: var(--pixso-fs-20);
  font-weight: 400;
  line-height: var(--pixso-lh-20);
}

.automotive-products__series-card-more {
  margin-top: var(--pixso-gap-xs);
  font-size: var(--pixso-fs-16);
  font-weight: 700;
  line-height: var(--pixso-lh-16);
}

@media (max-width: 1200px) {
  .automotive-products__hero {
    height: var(--pixso-hero-height);
    min-height: var(--pixso-hero-min);
  }

  .automotive-products__hero-copy {
    padding: 0 var(--pixso-fs-32);
  }

  .automotive-products__title {
    font-size: var(--pixso-fs-72);
    line-height: var(--pixso-lh-72);
  }

  .automotive-products__series {
    padding-top: var(--pixso-fs-72);
    padding-bottom: var(--automotive-products-series-bottom-tablet);
  }
}

@media (max-width: 768px) {
  .automotive-products__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
    background-size: cover;
    background-position: center top;
  }

  .automotive-products__hero-copy {
    padding: 0;
    gap: var(--pixso-fs-16);
  }

  .automotive-products__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .automotive-products__desc {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }

  .automotive-products__series {
    padding: var(--pixso-gap-lg) var(--pixso-page-padding) var(--automotive-products-series-bottom-mobile);
  }

  .automotive-products__series-grid {
    grid-template-columns: 1fr;
    gap: var(--pixso-gap-md);
  }

  .automotive-products__series-card {
    border-radius: var(--pixso-radius-md);
  }

  .automotive-products__series-card-copy {
    left: var(--pixso-gap-md);
    right: var(--pixso-gap-md);
    bottom: var(--pixso-gap-md);
  }

  .automotive-products__series-card-title {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .automotive-products__series-card-subtitle,
  .automotive-products__series-card-more {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }
}
</style>
